import { FormaPagamentoResgate } from '../../enum/parametros/forma-pagamento-resgate.enum';
import { IntervaloParcelamento } from '../../enum/parametros/intervalo-parcelamento.enum';
import { RegraPercentualResgate } from '../../enum/parametros/regra-percentual-resgate.enum';
import { TipoInstituto } from '../../enum/parametros/tipo-instituto.enum';
import {
  CreateInstitutoParamCommandDto,
  InstitutoParamResponseDto,
  PatchInstitutoParamCommandDto,
} from '../../models/parametros/instituto-param-api.model';
import {
  DocumentoTipoResponseDto,
  InstitutoParamDocResponseDto,
} from '../../models/parametros/instituto-param-doc-api.model';
import {
  ConfiguracaoInstituto,
  ProvedorAssinatura,
  ResgateConfiguracao,
  ResgateDocumento,
  ResgateDocumentoOpcao,
} from '../../models/parametros/resgate-configuracao.model';
import {
  InstitutoParamResgateResponseDto,
  PatchInstitutoParamResgateCommandDto,
  ProvedorAssinaturaResponseDto,
} from '../../models/parametros/resgate-configuracao-api.model';
import { extractIsoDate, toApiDateTime } from '../../utils/date-format.util';
import { RESGATE_CONFIGURACAO_PADRAO } from '../../utils/parametros/resgate-configuracao.defaults';

const TIPO_RESGATE: Record<RegraPercentualResgate, string> = {
  [RegraPercentualResgate.Total]: 'T',
  [RegraPercentualResgate.Parcial]: 'P',
};

const FORMA_PAGAMENTO: Record<FormaPagamentoResgate, string> = {
  [FormaPagamentoResgate.Unico]: 'U',
  [FormaPagamentoResgate.Parcelado]: 'P',
};

const INTERVALO_PARCELAMENTO: Record<IntervaloParcelamento, string> = {
  [IntervaloParcelamento.Mensal]: 'M',
  [IntervaloParcelamento.MensalAntecipacao]: 'A',
};

function doCodigo<T extends string>(codigos: Record<T, string>, codigo: string | null | undefined, padrao: T): T {
  const valor = Object.entries(codigos).find(([, item]) => item === codigo?.trim())?.[0];
  return (valor as T | undefined) ?? padrao;
}

/**
 * Os documentos vêm já resolvidos (`documentosDtoToDocumentos`). A última atualização é a mais
 * recente entre o instituto parametrizado e o resgate.
 */
export function resgateDtoToConfiguracaoInstituto(
  institutoParam: InstitutoParamResponseDto,
  resgate: InstitutoParamResgateResponseDto,
  documentos: ResgateDocumento[],
): ConfiguracaoInstituto {
  const padrao = structuredClone(RESGATE_CONFIGURACAO_PADRAO);
  const atualizacoes = [institutoParam.dt_ult_atu, resgate.dt_ult_atu, resgate.dt_inclusao].filter(
    (data): data is string => !!data,
  );

  return {
    instituto: TipoInstituto.Resgate,
    ultimaAtualizacao: (atualizacoes.sort().at(-1) ?? '').replace(' ', 'T'),
    configuracao: {
      status: institutoParam.fl_ativo ? 'Ativo' : 'Inativo',
      vigencia: {
        inicio: extractIsoDate(institutoParam.dt_ini_vig),
        fim: extractIsoDate(institutoParam.dt_fim_vig) || null,
      },
      descricao: resgate.ds_resgate?.trim() ?? '',
      percentual: { regra: doCodigo(TIPO_RESGATE, resgate.tp_resgate, padrao.percentual.regra) },
      tributacao: { irRegressivo: resgate.fl_ir_regressivo, irProgressivo: resgate.fl_ir_progressivo },
      pagamento: {
        forma: doCodigo(FORMA_PAGAMENTO, resgate.tp_forma_pagto, padrao.pagamento.forma),
        parcelasMinimas: resgate.num_min_parc ?? padrao.pagamento.parcelasMinimas,
        parcelasMaximas: resgate.num_max_parc ?? padrao.pagamento.parcelasMaximas,
        intervalo: doCodigo(INTERVALO_PARCELAMENTO, resgate.tp_intervalo_parc, padrao.pagamento.intervalo),
      },
      documentos,
      retencao: {
        valorMinimo: valorRetencao(resgate.vlr_min_retencao),
        valorMaximo: valorRetencao(resgate.vlr_max_retencao),
      },
      assinatura: {
        obrigatoria: resgate.fl_exige_assin,
        provedor: resgate.id_prov_assin == null ? '' : String(resgate.id_prov_assin),
        validacaoDuasEtapas: resgate.fl_valida_2_etapas,
      },
    },
  };
}

/** Instituto associado ativo já nasce disponível para o participante. */
export function configuracaoToCreateInstitutoParamCommand(
  planoId: number,
  idTipoInstituto: number,
  configuracao: ResgateConfiguracao,
): CreateInstitutoParamCommandDto {
  return {
    id_plano: planoId,
    id_tp_inst: idTipoInstituto,
    ...configuracaoToPatchInstitutoParamCommand(configuracao),
    fl_disp_participante: configuracao.status === 'Ativo',
    desc_param: configuracao.descricao.trim(),
  };
}

export function configuracaoToPatchInstitutoParamCommand(
  configuracao: ResgateConfiguracao,
): Required<Pick<PatchInstitutoParamCommandDto, 'dt_ini_vig' | 'dt_fim_vig' | 'fl_ativo'>> {
  return {
    dt_ini_vig: toApiDateTime(configuracao.vigencia.inicio),
    dt_fim_vig: configuracao.vigencia.fim ? toApiDateTime(configuracao.vigencia.fim) : null,
    fl_ativo: configuracao.status === 'Ativo',
  };
}

/** Parcelas só valem no parcelamento, e provedor e validação em duas etapas só com assinatura. */
export function configuracaoToResgateCommand(
  configuracao: ResgateConfiguracao,
): Required<PatchInstitutoParamResgateCommandDto> {
  const { pagamento, assinatura } = configuracao;
  const parcelado = pagamento.forma === FormaPagamentoResgate.Parcelado;

  return {
    tp_resgate: TIPO_RESGATE[configuracao.percentual.regra],
    fl_ir_regressivo: configuracao.tributacao.irRegressivo,
    fl_ir_progressivo: configuracao.tributacao.irProgressivo,
    tp_forma_pagto: FORMA_PAGAMENTO[pagamento.forma],
    num_min_parc: parcelado ? pagamento.parcelasMinimas : null,
    num_max_parc: parcelado ? pagamento.parcelasMaximas : null,
    tp_intervalo_parc: parcelado ? INTERVALO_PARCELAMENTO[pagamento.intervalo] : null,
    fl_exige_assin: assinatura.obrigatoria,
    id_prov_assin: assinatura.obrigatoria && assinatura.provedor ? Number(assinatura.provedor) : null,
    fl_valida_2_etapas: assinatura.obrigatoria && assinatura.validacaoDuasEtapas,
    ds_resgate: configuracao.descricao.trim(),
    vlr_min_retencao: configuracao.retencao.valorMinimo,
    vlr_max_retencao: configuracao.retencao.valorMaximo,
  };
}

/** Valor de retenção da API: nulo, ausente ou 0 (registros antigos) vira `null` (não informado). */
function valorRetencao(valor: number | null | undefined): number | null {
  const numero = Number(valor ?? 0);
  return numero > 0 ? numero : null;
}

export function provedorAssinaturaDtoToProvedor(dto: ProvedorAssinaturaResponseDto): ProvedorAssinatura {
  return { id: dto.id, nome: (dto.nome_exibicao ?? dto.nome ?? '').trim() };
}

export function documentoTipoDtoToOpcao(dto: DocumentoTipoResponseDto): ResgateDocumentoOpcao {
  return { id: dto.id, nome: (dto.nome_exibicao ?? dto.nome ?? '').trim() };
}

/**
 * Só os documentos ativos entram na configuração, com o nome do catálogo de tipos. Um tipo fora do
 * catálogo continua na lista (com nome genérico) para não ser inativado sem querer ao salvar.
 */
export function documentosDtoToDocumentos(
  documentos: readonly InstitutoParamDocResponseDto[],
  tipos: readonly DocumentoTipoResponseDto[],
): ResgateDocumento[] {
  const nomes = new Map(tipos.map((tipo) => [tipo.id, documentoTipoDtoToOpcao(tipo).nome]));
  return documentos
    .filter((documento) => documento.fl_ativo)
    .map((documento) => ({
      id: documento.id_doc_tipo,
      nome: nomes.get(documento.id_doc_tipo) || `Documento ${documento.id_doc_tipo}`,
    }));
}
